import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { EscrowInvitationAcceptDocument } from 'src/graphql/actions/escrow/escrowInvitationAccept.mutation';
import { EscrowInvitationRejectDocument } from 'src/graphql/actions/escrow/escrowInvitationReject.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { refetchAppProtections } from 'src/containers/Applications/Protections/api';

export function useAcceptRejectEscrowInvitation(
  escrowInvitationId: string | null,
  {
    onAcceptCompleted,
    onRejectCompleted,
  }: {
    onAcceptCompleted: () => void;
    onRejectCompleted: () => void;
  },
) {
  const [acceptEscrowInvitation, { loading: acceptLoading }] = useMutation(EscrowInvitationAcceptDocument);
  const [rejectEscrowInvitation, { loading: rejectLoading }] = useMutation(EscrowInvitationRejectDocument);

  const accept = async () => {
    if (!escrowInvitationId) return;

    try {
      const { data } = await acceptEscrowInvitation({ variables: { escrowInvitationId } });
      if (data?.escrowInvitationAccept?.success) {
        toast.success('Invitation accepted!', {
          description: 'Please follow the escrow onboarding steps to complete your escrow configuration.',
        });
        refetchAppProtections();
        onAcceptCompleted();
      } else {
        throw new ClientError(data?.escrowInvitationAccept?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const reject = async () => {
    if (!escrowInvitationId) return;

    try {
      const { data } = await rejectEscrowInvitation({ variables: { escrowInvitationId } });
      if (data?.escrowInvitationReject?.success) {
        toast.success('Invitation declined!', {
          description: "We'll notify the escrow creator that you do not want to continue with this escrow.",
        });
        onRejectCompleted();
      } else {
        throw new ClientError(data?.escrowInvitationReject?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return { accept, reject, acceptLoading, rejectLoading };
}
